import React, { useState, useEffect, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import { withHandlers, buildSignupArgs } from "utilities/formRegister";
import { resolveCompanyName, needsCompanyName } from "utilities/pendingSignup";
import { challengeApplies } from "utilities/authChallenge";
import { isExistingAccountError } from "utilities/inviteAuth";
import { useAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import axios from "axios";
import { Link } from "react-router-dom";
import TurnstileWidget, { isTurnstileConfigured } from "./TurnstileWidget";

const errorMessages = {
  "Firebase: Error (auth/email-already-in-use).":
    "The email address is already in use by another account. Please use a different email address.",
  "Firebase: Error (auth/invalid-login-credentials).":
    "The email or password is incorrect. Please try again.",
};

function AuthForm(props) {
  const auth = useAuth();
  const { toast } = useToast();
  // const [ip, setIP] = useState("");
  // const [tacAcknowledged, setTacAcknowledged] = useState(false);
  const [pending, setPending] = useState(false);

  // SOUND-715. A Turnstile token is a widget artifact, not a form field, so it
  // deliberately does NOT go through buildSignupArgs — that helper is the
  // narrow allowlist between the form and the handler, and widening it for
  // something the user never typed is how it stopped being obvious what it
  // guards. Held in a ref because the token can arrive between renders.
  const turnstileTokenRef = useRef(null);
  const turnstileWidgetRef = useRef(null);
  const signupContextRef = useRef(null);

  // SOUND-739. ONE answer to "does the challenge apply here", used by the submit
  // gate, the widget and the failure banner alike.
  //
  // These three used to decide it independently, and the gate's copy was missing
  // the invited-user exemption the widget's copy had — so on an invite link the
  // widget never mounted, the token ref stayed null, and the gate refused every
  // submit forever. Nothing reached the server, so nothing was logged; twelve
  // days and four dead invitations later, two people mentioned it.
  //
  // Do not re-derive either half at the call sites. That is the bug.
  const challengeRequired = challengeApplies({
    type: props.type,
    inviteEmail: props.inviteEmail,
    configured: isTurnstileConfigured(),
  });

  // SOUND-722. The panel needs its OWN widget: it replaces the form, so the
  // form's widget is unmounted and its token spent by the time Resend is
  // pressed. Without this, guarding /users/send-verification would make Resend
  // permanently impossible.
  const panelTokenRef = useRef(null);
  const panelWidgetRef = useRef(null);
  const handlePanelToken = useCallback((token) => {
    panelTokenRef.current = token;
  }, []);

  // Set once POST /users answers EMAIL_VERIFICATION_REQUIRED. The Firebase
  // account exists and the session is live — the person is mid-flow, not
  // rejected — so the form is replaced by a panel that can finish the job.
  const [awaitingVerification, setAwaitingVerification] = useState(null);
  const [resending, setResending] = useState(false);

  // SOUND-726. The panel has to be able to ASK for the business name.
  //
  // The name is carried across email confirmation in localStorage, because
  // provisioning happens after the person clicks the link — possibly on another
  // device. Confirming on a phone, in a different browser, in private mode
  // (where setItem throws), or after clearing storage all reach POST /users with
  // no name, as does a first Google sign-in, which never had a form.
  //
  // That state used to DELETE their Firebase account: the API answered
  // "companyName is required" through failClosedSignup, the client signed them
  // out, and their next login read "invalid credentials" with the signup gone
  // and no route back. pendingSignup.js has always carried a comment saying "the
  // panel asks for the name instead" — it never did. This is that field.
  const [panelCompanyName, setPanelCompanyName] = useState("");
  const [needsName, setNeedsName] = useState(false);

  // SOUND-726. Only ONE provisioning call may be in flight at a time.
  //
  // The watcher below polls every 5s AND listens on both `focus` and
  // `visibilitychange` — and returning to a tab fires both, back to back. With
  // no guard, two calls entered POST /users together. Prod, 2026-08-28: one
  // signup created TWO companies and TWO applications 108ms apart, the user
  // ended up owning the second, and the API's ten-minute auth cache had already
  // recorded the first — so every request afterwards answered 403 until the
  // cache expired, including the schema the registration form needs to render.
  //
  // A ref, not state: this has to be readable and writable synchronously within
  // one tick. Two events in the same tick would both see a stale state value.
  // The API is idempotent now as well; that is the real protection, this is
  // just not making it do the work.
  const provisioningRef = useRef(false);

  const handleTurnstileToken = useCallback((token) => {
    turnstileTokenRef.current = token;
  }, []);

  // SOUND-726. The widget has ALWAYS reported this and nobody listened.
  //
  // TurnstileWidget fires onError when the challenge script cannot load or the
  // challenge itself errors — a privacy extension, a network filter, a DNS
  // block on challenges.cloudflare.com. AuthForm passed only onToken, so the
  // signal was emitted and dropped, and the submit gate below then blocked on
  // the resulting null token with "give it a second and try again".
  //
  // For someone whose browser cannot reach Cloudflare, that is a PERMANENT dead
  // end wearing the words of a temporary one: press Register, read "try again",
  // press Register. Which is what "tried several attempts" looks like from the
  // outside, and it leaves no server-side trace at all because nothing is ever
  // sent.
  const [challengeFailed, setChallengeFailed] = useState(false);
  const handleTurnstileError = useCallback(() => setChallengeFailed(true), []);

  const {
    handleSubmit,
    register,
    formState: { errors },
    getValues,
    setError,
    clearErrors,
  } = useForm({
    defaultValues: {
      email: props.inviteEmail || "",
    },
  });

  // useEffect(() => {
  //   axios.get("https://api.ipify.org/?format=json").then((res) => {
  //     setIP(res.data.ip);
  //   });
  // }, []);

  const submitHandlersByType = {
    signin: ({ email, pass }) => auth.signin(email, pass).then(props.onAuth),
    // signup: ({ email, pass, termsAndConditionsObject }) =>
    //   auth.signup(email, pass, termsAndConditionsObject).then(props.onAuth),
    signup: ({ email, pass, companyName }) =>
      auth
        .signup(email, pass, companyName, turnstileTokenRef.current)
        .then(props.onAuth),
    forgotpass: ({ email }) =>
      auth.sendPasswordResetEmail(email, turnstileTokenRef.current).then(() => {
        setPending(false);
        toast({
          status: "success",
          title: "Password reset email sent",
        });
      }),
    changepass: ({ pass }) =>
      auth.confirmPasswordReset(pass).then(() => {
        setPending(false);
        toast({
          status: "success",
          title: "Your password has been changed",
        });
      }),
  };

  // SOUND-709. `companyName` MUST be destructured here. This function is the
  // narrow point of the whole form: react-hook-form hands it every registered
  // field, and whatever is not named here is dropped before any handler sees
  // it. The field rendered, validated and was tracked correctly — and then died
  // on this line, which is why the payload reaching POST /users was `{ email }`.
  const onSubmit = ({ email, pass, companyName }) => {
    // SOUND-722. Check for the token BEFORE anything happens, because signup's
    // first act is creating a Firebase account — and that account SURVIVES a
    // challenge failure by design (the refusal is retryable, so we must not
    // delete the account holding the verification link). Submitting a beat too
    // early therefore stranded the address: the retry hit "email already in
    // use" with no way forward. Refusing here costs a toast; getting it wrong
    // costs the address.
    if (challengeRequired && !turnstileTokenRef.current) {
      // SOUND-726: two different situations, and they need different words.
      //
      // We do NOT fail open here. The API refuses a missing token on purpose
      // (SOUND-722 — absence is the clearest bot tell we get), so letting the
      // submit through would only move the dead end server-side, and on the
      // signup path it would burn a Firebase account reaching it. Refusing is
      // right; refusing while implying it is a timing problem is not.
      toast({
        status: "destructive",
        title: challengeFailed
          ? "We could not load the human-verification check"
          : "Still checking you are human — give it a second and try again.",
        ...(challengeFailed && {
          description:
            "It is usually an ad blocker or privacy extension blocking " +
            "challenges.cloudflare.com. Allow it for this page, or try another " +
            "browser. Still stuck? Email support@sound-commerce.com and we will " +
            "set your account up directly.",
        }),
      });
      return;
    }

    setPending(true);
    // Kept so the verification panel can finish provisioning later. The form is
    // unmounted by then, and the API requires companyName on the self-serve
    // path — auth.js also stashes it in localStorage for a cross-tab return.
    signupContextRef.current = { email, companyName };

    // const termsAndConditionsObject = tacAcknowledged
    //   ? {
    //       termsAndConditionsAcknowledged: new Date(),
    //       ipAddress: ip,
    //       browser: navigator.userAgent,
    //     }
    //   : null;

    const payload = { email, pass };

    if (props.type === "signup") {
      submitHandlersByType
        .signup(buildSignupArgs({ ...payload, companyName }))
        .catch(handleError);
    } else {
      submitHandlersByType[props.type](payload).catch(handleError);
    }
  };

  const handleError = (error) => {
    setPending(false);

    // SOUND-752, dead end 1. An invited address that already has an account
    // cannot "use a different email" — the field is read-only. Signing in with
    // it accepts the invitation, so send them there with the invite intact.
    if (props.inviteEmail && props.onExistingAccount && isExistingAccountError(error)) {
      toast({
        status: "info",
        title: "You already have a Sound account with this email.",
        description: "Sign in to accept your invitation.",
      });
      props.onExistingAccount();
      return;
    }

    // SOUND-715: two refusals are RECOVERABLE and must not read as failure.
    // The session and the Firebase account are still alive in both cases.
    if (error?.code === "EMAIL_VERIFICATION_REQUIRED") {
      // signupContextRef is only populated by a signup submit. This panel is
      // also reachable from SIGN-IN — someone who signed up, never confirmed,
      // and came back — where it would otherwise render "We sent a link to "
      // with nothing after it. The form's own email field is right either way.
      setAwaitingVerification({
        email:
          signupContextRef.current?.email || getValues("email") || auth.user?.email,
      });
      return;
    }

    // SOUND-726. Recoverable, and the account is still alive — we simply do not
    // know the business name. Reachable from sign-in (resuming a signup started
    // on another device) and from a first Google sign-in.
    if (error?.code === "COMPANY_NAME_REQUIRED") {
      setNeedsName(true);
      setAwaitingVerification({
        email:
          signupContextRef.current?.email || getValues("email") || auth.user?.email,
      });
      return;
    }

    if (error?.code === "CHALLENGE_FAILED") {
      // A Turnstile token is single-use and expires after ~5 minutes, so the
      // one in hand is now spent whatever the reason. Reset, or the retry
      // re-sends the same dead token and fails identically forever.
      turnstileWidgetRef.current?.reset();

      // On SIGNUP the Firebase account already exists by the time we get here,
      // and it deliberately survives a retryable refusal. Leaving the form up
      // offers a route that cannot work — re-submitting hits "email already in
      // use" and dead-ends. Move to the panel instead: it carries its own
      // widget, so its Resend can get a fresh token and recover.
      if (props.type === "signup") {
        setAwaitingVerification({
          email: signupContextRef.current?.email || getValues("email"),
        });
        return;
      }

      toast({
        status: "destructive",
        title: error.message || "Could not verify you are human. Please try again.",
      });
      return;
    }

    const errorMessage =
      errorMessages[error.message] ||
      error?.message ||
      "An unexpected error occurred. Please try again.";
    toast({
      status: "destructive",
      title: errorMessage,
    });
  };

  // Finish provisioning once the address is confirmed. Shared by the manual
  // button and the poll below.
  // Ask the SERVER whether the address is confirmed, by simply trying to
  // finish. Do not ask the browser.
  //
  // This first asked the Firebase SDK client-side and only called the API once
  // it agreed. On DEV that produced the exact failure this is replacing: the
  // Firebase BACKEND read emailVerified:true while the browser kept insisting
  // otherwise, so the panel told someone who had just clicked the link that
  // they had not. Verified in the admin SDK — the account was confirmed and the
  // client's lastRefreshTime was still its creation time.
  //
  // The client's view was never the authority anyway. POST /users re-checks
  // with getAuth().getUser() on every call, and that check is the thing
  // actually gating the data — so the only honest question is "does the server
  // let me through yet", and the only reliable way to ask is to try.
  const completeVerifiedSignup = useCallback(
    async ({ silent = false } = {}) => {
      // Everywhere the panel might know the name from. auth.js falls back to
      // the localStorage stash when both are empty, so passing "" is safe — it
      // is only a downgrade if it OVERRIDES a good value, which
      // resolveCompanyName's trim-then-first-non-empty ordering prevents.
      const nameSources = {
        typed: panelCompanyName,
        remembered: signupContextRef.current?.companyName,
      };

      // Do not fire a request we already know will be refused. The poll below
      // runs every 5s; once the API has told us it needs a name, hammering it
      // until the person types one is noise in the logs and the rate limiter.
      if (needsName && needsCompanyName(nameSources)) {
        if (!silent) {
          toast({
            status: "destructive",
            title: "Please enter your business name to finish.",
          });
        }
        return false;
      }

      // SOUND-726: one at a time. See provisioningRef above — two overlapping
      // calls each minted a company.
      if (provisioningRef.current) return false;
      provisioningRef.current = true;

      try {
        const user = await auth.provisionAccount({
          companyName: resolveCompanyName(nameSources),
          // Only needed if the address turns out NOT to be confirmed — the
          // server's challenge lives inside that branch and is skipped once it
          // is. Sent anyway so a mistaken attempt is refused for the honest
          // reason rather than looking like a bot.
          turnstileToken: panelTokenRef.current,
        });
        props.onAuth?.(user);
        return true;
      } catch (error) {
        const code = error?.response?.data?.code || error?.code;

        // SOUND-726: not a failure, a question. Reveal the field and let them
        // answer it. Never destructive now, so the account is still there.
        if (code === "COMPANY_NAME_REQUIRED") {
          setNeedsName(true);
          if (!silent) {
            toast({
              status: "destructive",
              title: "Almost there — we just need your business name.",
            });
          }
          return false;
        }

        // Still waiting. Not an error — the expected answer until they click.
        if (code === "EMAIL_VERIFICATION_REQUIRED" || code === "CHALLENGE_FAILED") {
          if (!silent) {
            toast({
              status: "destructive",
              title: "That email is not confirmed yet. Check your inbox, then try again.",
            });
          }
          return false;
        }
        throw error;
      } finally {
        // Released on every exit — success, refusal or throw. Leaving it set
        // would wedge the panel permanently on the one path that matters.
        provisioningRef.current = false;
      }
    },
    [auth, props, toast, panelCompanyName, needsName]
  );

  // Keep the latest callback in a ref so the watcher below can depend ONLY on
  // whether the panel is open.
  //
  // This is not a style preference, it is the bug. `completeVerifiedSignup` is
  // a useCallback over [auth, props, toast]; `useAuthProvider` returns a plain
  // object literal (new identity every render, never memoized) and `props` is a
  // fresh object each render too. So the callback changed identity on nearly
  // every render, the effect tore down and recreated its interval each time,
  // and the 5s timer reset before it ever completed a tick. Confirmed against
  // DEV: two POST /users in 50 seconds, when a live poll would have made more.
  const completeRef = useRef(completeVerifiedSignup);
  useEffect(() => {
    completeRef.current = completeVerifiedSignup;
  }, [completeVerifiedSignup]);

  // Watch for the address being confirmed, so someone who clicks the link in
  // another tab or on their phone is carried through without coming back to
  // press anything.
  useEffect(() => {
    if (!awaitingVerification) return undefined;

    // Ask the browser first, and only call the API once it believes the address
    // is confirmed.
    //
    // SOUND-722: POST /users now treats a missing token as a bot tell, and this
    // poll fires every few seconds. Calling it blindly would manufacture a
    // steady stream of false CHALLENGE_FAILED events and bury the real signal.
    // Deciding locally is safe because nothing is created here — the server
    // re-checks before it writes, so a wrong local answer costs one refused
    // request, not a bad record.
    const check = async () => {
      const verified = await auth.refreshVerification().catch(() => false);
      if (!verified) return;
      await completeRef.current({ silent: true }).catch(() => {
        // Server disagreed, or a transient failure. The manual button reports
        // properly; a background check must stay quiet.
      });
    };

    // Immediately, then on a timer. Waiting a full interval before the first
    // look is dead time for someone who confirmed before the panel rendered.
    check();
    const id = setInterval(() => { check(); }, 5000);

    // The important one. Clicking the link means LEAVING this tab, so coming
    // back is the strongest possible signal that something changed — far
    // better than waiting out a timer that a backgrounded tab may be throttling
    // to once a minute anyway.
    const onWake = () => {
      if (document.visibilityState === "visible") check();
    };
    window.addEventListener("focus", onWake);
    document.addEventListener("visibilitychange", onWake);

    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onWake);
      document.removeEventListener("visibilitychange", onWake);
    };
  }, [awaitingVerification]);

  // SOUND-715. Replaces the form once the API says the address needs
  // confirming. Deliberately NOT a toast-and-stay: the account already exists,
  // so re-submitting the form would only hit "email already in use" and read as
  // a dead end. This panel is the only route forward, so it has to BE the page.
  if (awaitingVerification) {
    return (
      <div className="space-y-5 text-left">
        <div>
          <h3 className="text-lg font-semibold mb-1">Confirm your email</h3>
          <p className="text-sm text-muted-foreground">
            We sent a link to{" "}
            <span className="font-medium text-foreground">
              {awaitingVerification.email}
            </span>
            . Open it to finish setting up your account — you can leave this page
            open, we will pick it up automatically.
          </p>
        </div>

        {/* SOUND-726. Only rendered when we genuinely do not have the name: they
            confirmed on another device, cleared storage, are in private mode, or
            signed in with Google. Everyone else never sees it. */}
        {needsName && (
          <div>
            <label
              htmlFor="panelCompanyName"
              className="text-left block mb-1 text-sm"
            >
              Business name
            </label>
            <input
              id="panelCompanyName"
              type="text"
              autoFocus
              placeholder="Acme Widgets LLC"
              value={panelCompanyName}
              onChange={(e) => setPanelCompanyName(e.target.value)}
              className="w-full px-4 py-2 rounded-md border border-border bg-card focus:outline-none focus:ring-2 focus:ring-brand"
            />
            <p className="mt-1 text-sm text-muted-foreground">
              We could not carry this over from the device you signed up on.
            </p>
          </div>
        )}

        <button
          type="button"
          disabled={pending}
          onClick={() => {
            setPending(true);
            completeVerifiedSignup()
              .catch(handleError)
              .finally(() => setPending(false));
          }}
          className="w-full inline-block px-6 py-3 bg-brand text-background text-sm font-semibold rounded-lg hover:bg-brand-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {pending
            ? "..."
            : needsName
            ? "Finish setting up my account"
            : "I have confirmed my email"}
        </button>

        {isTurnstileConfigured() && (
          <TurnstileWidget
            ref={panelWidgetRef}
            onToken={handlePanelToken}
            onError={handleTurnstileError}
            className="pt-1"
          />
        )}

        <p className="text-sm text-muted-foreground">
          Nothing in your inbox?{" "}
          <button
            type="button"
            disabled={resending}
            onClick={() => {
              setResending(true);
              auth
                .resendVerification(panelTokenRef.current)
                .then(() => {
                  // Single-use: spend it, then ask for another so a second
                  // Resend is not silently impossible.
                  panelWidgetRef.current?.reset();
                  toast({ status: "success", title: "Verification email sent" });
                })
                .catch((error) =>
                  toast({
                    status: "destructive",
                    // The API now says WHY. A throttle is temporary and worth
                    // waiting out; a generic failure is not the same thing, and
                    // telling someone to "try again" against a throttle just
                    // makes them press it more.
                    title:
                      error?.response?.data?.error ||
                      "Could not resend just now. Try again in a moment.",
                  })
                )
                .finally(() => setResending(false));
            }}
            className="text-brand underline disabled:opacity-60"
          >
            {resending ? "Sending..." : "Send it again"}
          </button>
          . Check your spam folder too.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {["signup", "signin", "forgotpass"].includes(props.type) && (
        <div>
          <label htmlFor="email" className="text-left block mb-1 text-sm">
            Email
          </label>
          <input
            type="email"
            {...withHandlers(register("email", { required: "Please enter an email" }), {
              onChange: () => clearErrors("email"),
            })}
            readOnly={!!props.inviteEmail}
            className={`w-full px-4 py-2 rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-brand ${
              props.inviteEmail
                ? "bg-muted/50 text-foreground cursor-not-allowed opacity-75"
                : "bg-card"
            }`}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>
      )}

      {/* SOUND-631: capture the business name AT SIGNUP, and REQUIRE it. It used
          to be collected only later on the application, so a merchant who
          abandoned the form left a permanently nameless company — 15 of them in
          prod, accruing ~2/month since March.
          Required rather than optional so the invariant is real: every company
          created from here has a name, and therefore a derived slug (SOUND-278).
          Only shown for SELF-SERVE signup — an invited user joins an existing
          company, where a name would be meaningless. */}
      {props.type === "signup" && !props.inviteEmail && (
        <div>
          <label htmlFor="companyName" className="text-left block mb-1 text-sm">
            Business name
          </label>
          <input
            type="text"
            placeholder="Acme Widgets LLC"
            {...withHandlers(
              register("companyName", {
                required: "Please enter your business name",
                validate: (v) =>
                  String(v ?? "").trim().length > 0 ||
                  "Please enter your business name",
              }),
              { onChange: () => clearErrors("companyName") }
            )}
            className="w-full px-4 py-2 rounded-md border border-border bg-card focus:outline-none focus:ring-2 focus:ring-brand"
          />
          {errors.companyName && (
            <p className="mt-1 text-sm text-red-600">
              {errors.companyName.message}
            </p>
          )}
        </div>
      )}

      {["signup", "signin", "changepass"].includes(props.type) && (
        <div>
          <label htmlFor="password" className="text-left block mb-1 text-sm">
            Password
          </label>
          <input
            type="password"
            {...register("pass", { required: "Please enter a password" })}
            className="w-full px-4 py-2 rounded-md bg-card border border-border focus:outline-none focus:ring-2 focus:ring-brand"
          />
          {errors.pass && (
            <p className="mt-1 text-sm text-red-600">{errors.pass.message}</p>
          )}
        </div>
      )}

      {["signup", "changepass"].includes(props.type) && (
        <div>
          <label htmlFor="password" className="text-left block mb-1 text-sm">
            Confirm Password
          </label>
          <input
            type="password"
            placeholder="Confirm Password"
            {...register("confirmPass", {
              required: "Please enter your password again",
              validate: (value) =>
                value === getValues().pass ||
                "This doesn't match your password",
            })}
            className="w-full px-4 py-2 rounded-md bg-card border border-border focus:outline-none focus:ring-2 focus:ring-brand"
          />
          {errors.confirmPass && (
            <p className="mt-1 text-sm text-red-600">
              {errors.confirmPass.message}
            </p>
          )}
        </div>
      )}

      {/* {props.type === "signup" && (
        <div className="mt-2">
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              {...withHandlers(register("termsAndConditions", { required: true }), {
                onChange: () => setTacAcknowledged(!tacAcknowledged),
              })}
              className="mt-1"
            />
            <span>
              By checking this box, you agree to our{" "}
              <Link
                to="/legal/terms-of-use"
                className="text-brand underline"
              >
                Terms of Use
              </Link>{" "}
              and{" "}
              <Link
                to="/legal/privacy-policy"
                className="text-brand underline"
              >
                Privacy Policy
              </Link>
              .
            </span>
          </label>
          {errors.termsAndConditions && (
            <p className="mt-1 text-sm text-red-600">
              Please accept the Terms of Use and Privacy Policy
            </p>
          )}
        </div>
      )} */}

      {/* Bot challenge on the forms that make us SEND MAIL (SOUND-715/722).
          WHICH those are, and why, lives in utilities/authChallenge.js — the one
          place that decides it. Restating the rule here is what broke invited
          signup for twelve days (SOUND-739). */}
      {challengeRequired && (
        <TurnstileWidget
          ref={turnstileWidgetRef}
          onToken={handleTurnstileToken}
          onError={handleTurnstileError}
          className="pt-1"
        />
      )}

      {/* SOUND-726: durable, not a toast. Someone in this state will press the
          button repeatedly, and a message that fades tells them nothing the
          third time. It also names a route out that does not depend on the
          challenge working at all. */}
      {challengeFailed && challengeRequired && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">
          <p className="font-medium mb-1">
            We could not load the human-verification check
          </p>
          <p className="text-muted-foreground">
            This is almost always an ad blocker or privacy extension blocking{" "}
            <code className="text-xs">challenges.cloudflare.com</code>. Allow it
            for this page, or try another browser. If it keeps happening, email{" "}
            <a
              className="text-brand underline"
              href="mailto:support@sound-commerce.com"
            >
              support@sound-commerce.com
            </a>{" "}
            and we will set your account up directly.
          </p>
        </div>
      )}

      <div className="pt-2">
        <button
          type="submit"
          disabled={pending}
          // re-enable when TandCs are complete
          // disabled={pending || (props.type === "signup" && !tacAcknowledged)}
          className="w-full inline-block px-6 py-3 bg-brand text-background text-sm font-semibold rounded-lg hover:bg-brand-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {pending ? "..." : props.buttonAction}
        </button>
      </div>
    </form>
  );
}

export default AuthForm;
